import { Close, MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React, { ChangeEvent, ReactNode, useCallback, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useDebouncedCallback } from 'use-debounce';

import { ModifyQueryParamsAction, useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';

import { PaginationFilter } from './types/PaginationFilter';

export type SearchFilterProps = {
    readonly className?: string;
    readonly onChange: Function;
    /** A PaginationFilter object  */
    readonly paginationFilter: PaginationFilter;
    /** Used to display result count, or any text you want really  */
    readonly resultsDisplay?: any;
    readonly searchLabel?: ReactNode;
    /** Text to display in the search bar when nothing is entered */
    readonly searchPlaceholder?: string;
    /** Should modify query params */
    readonly shouldModifyQueryParams?: boolean;
    readonly testid?: string;
};

export const SearchFilter = ({
    className,
    onChange,
    paginationFilter,
    resultsDisplay,
    searchLabel,
    searchPlaceholder,
    shouldModifyQueryParams,
    testid = 'searchFilter',
}: SearchFilterProps) => {
    const modifyQueryParams = useModifyQueryParams();

    const [search, setSearch] = useState(paginationFilter.keyword);

    const onSearchChange = useCallback(
        (filters?: { keyword: string; page: number }) => {
            if (shouldModifyQueryParams) {
                if (filters?.keyword === '') {
                    modifyQueryParams({
                        action: ModifyQueryParamsAction.REMOVE,
                        params: 'keyword',
                    });
                } else {
                    modifyQueryParams({
                        action: ModifyQueryParamsAction.ADD,
                        query: { keyword: filters?.keyword },
                    });
                }
            }
            onChange(filters);
        },
        [shouldModifyQueryParams, onChange, modifyQueryParams]
    );
    const debouncedSearchChanged = useDebouncedCallback(onSearchChange, 300);

    const handleSearchChanged = useCallback(
        ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
            const { value } = currentTarget;
            setSearch(value);
            debouncedSearchChanged({ keyword: value, page: 1 });
        },
        [debouncedSearchChanged]
    );

    const handleSearchClear = useCallback(() => {
        setSearch('');
        onSearchChange({ keyword: '', page: 1 });
    }, [onSearchChange]);

    return (
        <div
            className={cn('flex flex-col', {
                'gap-xs': resultsDisplay !== undefined,
            })}
        >
            <TextInput
                aria-label="Search Filter"
                className={className}
                data-testid={testid}
                labelText={searchLabel}
                onChange={handleSearchChanged}
                placeholder={searchPlaceholder}
                small
                suffix={
                    paginationFilter.keyword ? (
                        <Close
                            className="cursor-pointer text-link-primary"
                            onClick={handleSearchClear}
                            size="sm"
                        />
                    ) : (
                        <MagnifyingGlass
                            className="self-center"
                            size="sm"
                        />
                    )
                }
                value={search}
            />
            {resultsDisplay}
        </div>
    );
};

export default React.memo<SearchFilterProps>(SearchFilter, isEqual);
